import {
	makeGetAdditionalCoverage,
	makeGetBeneficiaryDocumentUrl,
	makeGetDebtSettlementStatementUrl,
	makeGetHelpDeskSacLinkPage,
	makeGetHospitalizationUrl,
	makeGetPerksClubLink,
	makeGetPinSsUrl,
	makeGetPortabilityCardUrl,
	makeGetRefundOdonto,
	makeGetServiceCenterUrl,
	makeGetStayLetterUrl,
	makeGetSupportLink,
	makeGetUnitsUrl,
} from 'main/factories/usecases';

import {
	ConsultProtocolsEnum,
	ChangePlanEnum,
	ContractAdaptationEnum,
	ContractReactivationEnum,
	FeaturesEnum,
	FranchisesTableEnum,
	GuidesEnum,
	HelpCenterEnum,
	MyHospitalizationFlowEnum,
	MyPaymentsEnum,
	PerksEnum,
	TokenEnum,
} from 'presentation/constants';
import {ChangeDueDateEnum} from 'modules/financial/routes/mapRoutesFinancial';
import {useContractStatus, useHostName} from 'presentation/hooks';

import {ExternalServicesTaggingEnum, ServicesTaggingEnum} from 'main/analytics/gaTagsEnum';
import {makeGetWhoIndicatesLink} from 'main/factories/usecases/private/remote-home/get-who-indicates';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {
	CategoriesEnum,
	ExternalServicesTitlesEnum,
	InvoicesNFLinksEnum,
	Service,
	ServiceList,
	ServicesCenterTitlesEnum,
	ServicesTitlesEnum,
} from './types';

import {
	FINANCIAL_SUPPORT_WHATSAPP_LINK,
	FINANCIAL_SUPPORT_WHATSAPP_LINK_PPO,
} from 'presentation/pages/private/help/constants/channelsContacts';
import {useAccountInformation} from '../useAccountInformation';
import {useMyHospitalizationFeatureFlag} from '../useMyHospitalizationFeatureFlag';
import {useNewBillsBemobiFeatureFlag} from '../useNewBillsBemobiFeatureFlag';
import {ServicesTypesEnum} from '../useServicesListForShortcuts/types';
import {findItemByQuery} from '../utils/findItemByQuery';
import {useCongenereEligibilityStore} from '../../store/useCongenereEligibilityStore';

export function useServicesList(query = '') {
	const {isGndiMinas, isGndiSaoPaulo, dataHostNameState} = useHostName();

	const {
		flPlanoSomenteHospitalar,
		businessPlan,
		healthOnly,
		odontoOnly,
		healthOdonto,
		flAdministradora,
		flPad,
		accesses,
		isResponsibleWithoutBenefits,
		isPPOPlan,
		isHolder,
	} = useAccountInformation();
	const financialSupportWhatsappLink = isPPOPlan
		? FINANCIAL_SUPPORT_WHATSAPP_LINK_PPO
		: FINANCIAL_SUPPORT_WHATSAPP_LINK;
	const {isValid} = useContractStatus();
	const congenereEligibility = useCongenereEligibilityStore(state => state.congenereEligibility);

	const {isEnabled: isMyHospitalizationFeatureFlagEnabled} = useMyHospitalizationFeatureFlag();

	const {isEnabled: isIrDemonstrativeFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_INCOME_TAX,
	);

	const {isEnabled: isAccordionServicesFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ACCORDION_SERVICES,
	);

	const {isEnabled: isAdmContractAmendmentEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ADM_CONTRACT_AMENDMENT,
	);

	const {isEnabled: isAdmContractReactivationEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ADM_REACTIVATION_CALCELED_CONTRACT,
	);

	const {isEnabled: isAdmChangePlanEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ADM_CHANGE_PLAN,
	);
	const {isEnabled: isAdmChangeDueDateEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ADM_CHANGE_DUE_DATE,
	);

	const {isEnabled: isShowOmbudsmanInChannelsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SHOW_OMBUDSMAN_IN_CHANNELS,
	);

	const {isEnabled: isHidePaymentSupportEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_HIDE_PAYMENT_SUPPORT_ICON,
	);

	const {isEnabled: newBillsFlagIsEnabled} = useNewBillsBemobiFeatureFlag();
	const {isEnabled: isServiceNfEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_SERVICE_NF);

	const {isEnabled: isLifePlaceEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_LIFEPLACE);

	const isIndividualPlan = flPad || (!businessPlan && !flAdministradora);
	const showInvoicesOption =
		isIndividualPlan && !isResponsibleWithoutBenefits && isServiceNfEnabled;
	const showServiceCenterLinks = isIndividualPlan && isValid && !congenereEligibility;

	const showHospitalizationService =
		(healthOdonto || (healthOnly && !flPlanoSomenteHospitalar)) &&
		!isResponsibleWithoutBenefits;
	const showFranchisesTable =
		isGndiSaoPaulo && accesses?.hasFranchise && !isResponsibleWithoutBenefits;
	const showRefundOdonto = isGndiSaoPaulo && !healthOnly && !isResponsibleWithoutBenefits;

	const showNonGndiHealthServices = !isGndiSaoPaulo && !odontoOnly;

	const showBenefitsForGndiSaoPaulo =
		isGndiSaoPaulo && accesses?.hasBenefits && !isResponsibleWithoutBenefits;

	const showForGndiMinasOrSaoPaulo = isGndiMinas || isGndiSaoPaulo;

	const helpDeskOrSupportEndpoint = isGndiSaoPaulo
		? makeGetHelpDeskSacLinkPage
		: makeGetSupportLink;

	const shouldShowTab = flPad || (!businessPlan && !flAdministradora);
	const showIRBase = shouldShowTab || (businessPlan && accesses.incomeTaxEmploy);
	const showIrDemonstrative =
		!isIrDemonstrativeFeatureFlagEnabled &&
		isValid &&
		showIRBase &&
		!isResponsibleWithoutBenefits;

	const showBemobiFlow = newBillsFlagIsEnabled
		? ServicesTitlesEnum.INVOICES
		: ServicesTitlesEnum.BILLET;

	const getInvoicesNFLinkByProduct = (product: string): string => {
		const linkMap: Record<string, string> = {
			hap: InvoicesNFLinksEnum.HAPVIDA,
			ccg: InvoicesNFLinksEnum.CCG,
			gndiminas: InvoicesNFLinksEnum.NDIMINAS,
			clinipam: InvoicesNFLinksEnum.CLINIPAM,
			gndi: InvoicesNFLinksEnum.NDI_SP,
		};

		return linkMap[product] || InvoicesNFLinksEnum.HAPVIDA;
	};

	const OLD_SERVICES_LIST: ServiceList[] = [
		{
			category: CategoriesEnum.HEALTH_APPOINTMENTS,
			list: [
				{
					title: ServicesTitlesEnum.MY_HOSPITALIZATION,
					icon: 'local-hospital-filled',
					endpoint: isMyHospitalizationFeatureFlagEnabled
						? undefined
						: makeGetHospitalizationUrl,
					route: isMyHospitalizationFeatureFlagEnabled
						? MyHospitalizationFlowEnum.HOME
						: undefined,
					show: showHospitalizationService,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MINHA_INTERNACAO,
				},
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_HEALTH,
					icon: 'pin-drop-filled',
					route: GuidesEnum.SERVICE_NETWORK_HEALTH,
					show: showNonGndiHealthServices && !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_MEDICO,
				},
			],
		},
		{
			category: CategoriesEnum.ODONTO_APPOINTMENTS,
			list: [
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_ODONTO,
					icon: 'pin-drop-filled',
					route: GuidesEnum.SERVICE_NETWORK_ODONTO,
					show: !healthOnly && !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_ODONTO,
				},
				{
					title: ServicesTitlesEnum.FRANCHISES_TABLE,
					icon: 'table-rows-filled',
					route: FranchisesTableEnum.FRANCHISES_TABLE,
					show: showFranchisesTable,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_TABELA_FRANQUIAS,
				},
				{
					title: ServicesTitlesEnum.REFUND_ODONTO,
					icon: 'odonto-exchange-filled',
					endpoint: makeGetRefundOdonto,
					show: showRefundOdonto,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_REEMBOLSO_ODONTO,
				},
			],
		},
		{
			category: CategoriesEnum.FINANCIAL_AND_DOCS,
			list: [
				{
					title: showBemobiFlow,
					icon: 'receipt-long-filled',
					route: MyPaymentsEnum.OPEN_BILLS,
					show: isIndividualPlan && !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_BOLETOS,
				},
				{
					title: ServicesTitlesEnum.COPAYMENT_STATEMENT,
					icon: 'text-box-outline',
					route: MyPaymentsEnum.COPARTICIPATION,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_EXTRATO_DE_CO_PARTICIPACAO,
				},
				{
					title: ServicesTitlesEnum.INCOME_TAX_STATEMENT,
					icon: 'lion-filled',
					route: MyPaymentsEnum.INCOME_TAX,
					show: showIrDemonstrative,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_DEMOSTRATIVO_IR,
				},
				{
					title: ExternalServicesTitlesEnum.INVOICES,
					icon: 'barcode-default',
					externalLink: getInvoicesNFLinkByProduct(dataHostNameState.product),
					show: showInvoicesOption,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_NOTAS_FISCAIS,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_DUE_DATE,
					icon: 'event-repeat',
					endpoint: isAdmChangeDueDateEnabled ? undefined : makeGetServiceCenterUrl,
					route: isAdmChangeDueDateEnabled
						? ChangeDueDateEnum.CHANGE_DUE_DATE
						: undefined,
					show: showServiceCenterLinks && isHolder && !odontoOnly,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_ALTERAR_DATA_DE_VENCIMENTO,
				},
				{
					title: ExternalServicesTitlesEnum.DEBT_SETTLEMENT_STATEMENT,
					icon: 'gavel-filled',
					endpoint: makeGetDebtSettlementStatementUrl,
					show: true,
					type: ServicesTypesEnum.OTHERS,
					dataTagId:
						ExternalServicesTaggingEnum.CLICK_DEMONSTRATIVO_DE_QUITACAO_DE_DEBITOS,
				},
				{
					title: ServicesCenterTitlesEnum.DECLARATIONS_AND_DOCUMENTS,
					icon: 'pending-action',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_DECLARACOES_DOCUMENTOS,
				},
				{
					title: ExternalServicesTitlesEnum.BENEFICIARY_DOCUMENT,
					icon: 'document-scanner',
					endpoint: makeGetBeneficiaryDocumentUrl,
					show: isGndiSaoPaulo,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_DOCS_E_FORMS,
				},
				{
					title: ExternalServicesTitlesEnum.FINANCIAL_SUPPORT,
					icon: 'chat-filled',
					externalLink: financialSupportWhatsappLink,
					show: !(businessPlan && isHidePaymentSupportEnabled),
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ATENDIMENTO_FINANCEIRO_WHATSAPP,
				},
			],
		},
		{
			category: CategoriesEnum.PERKS,
			list: [
				{
					title: ServicesTitlesEnum.PERKS_CLUB,
					icon: 'basket',
					endpoint: makeGetPerksClubLink,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CLUBE_DE_VANTAGENS,
				},
				{
					title: ServicesTitlesEnum.ADDITIONAL_COVERAGE,
					icon: 'circular-star',
					endpoint: makeGetAdditionalCoverage,
					show: showBenefitsForGndiSaoPaulo,
					type: ServicesTypesEnum.OTHERS,
					openInIframe: true,
					dataTagId: ExternalServicesTaggingEnum.CLICK_COBERTURAS_ADICIONAIS,
				},
				{
					title: ServicesTitlesEnum.RECOMMENDATION,
					icon: 'add-reaction-filled',
					endpoint: makeGetWhoIndicatesLink,
					disableCache: true,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_QUEM_INDICA,
				},
			],
		},
		{
			category: CategoriesEnum.PORTABILITY_AND_PERMANENCE,
			list: [
				{
					title: ExternalServicesTitlesEnum.PIN_SS,
					icon: 'keyboard-filled',
					endpoint: makeGetPinSsUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_PIN_SS,
				},
				{
					title: ExternalServicesTitlesEnum.STAY_LETTER,
					icon: 'mark-as-read',
					endpoint: makeGetStayLetterUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					isBlob: true,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_PERMANENCIA,
				},
				{
					title: ExternalServicesTitlesEnum.PORTABILITY_CARD,
					icon: 'mark-as-unread',
					endpoint: makeGetPortabilityCardUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_DE_PORTABILIDADE,
				},
			],
		},
		{
			category: CategoriesEnum.SUPPORT,
			list: [
				{
					title: ServicesTitlesEnum.UNITS,
					icon: 'location-on-filled',
					endpoint: makeGetUnitsUrl,
					show: isGndiSaoPaulo && !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_REDE_PROPRIA,
				},
				{
					title: ExternalServicesTitlesEnum.SUPPORT,
					icon: 'verified-user',
					endpoint: helpDeskOrSupportEndpoint,
					show:
						showForGndiMinasOrSaoPaulo &&
						!isResponsibleWithoutBenefits &&
						isShowOmbudsmanInChannelsEnabled,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_OUVIDORIA,
				},
				{
					title: ServicesTitlesEnum.SERVICE_CHANNELS,
					icon: 'help-filled',
					route: HelpCenterEnum.HELP,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_PRECISA_DE_AJUDA,
					keywords: ['ajuda'],
				},
			],
		},
		{
			category: CategoriesEnum.MANAGEMENT,
			list: [
				{
					title: ServicesTitlesEnum.CONSULT_PROTOCOLS,
					icon: 'content-paste-search-filled',
					route: ConsultProtocolsEnum.CONSULT_PROTOCOLS,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_CONSULTA_DE_PROTOCOLOS,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_PLAN,
					icon: 'books-filled',
					endpoint: isAdmChangePlanEnabled ? undefined : makeGetServiceCenterUrl,
					route: isAdmChangePlanEnabled ? ChangePlanEnum.CHANGE_PLAN : undefined,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MUDANCA_DE_PLANO,
				},
				{
					title: ServicesCenterTitlesEnum.CONTRACT_ADAPTATION,
					icon: 'source-filled',
					endpoint: isAdmContractAmendmentEnabled ? undefined : makeGetServiceCenterUrl,
					route: isAdmContractAmendmentEnabled
						? ContractAdaptationEnum.CONTRACT_ADAPTATION
						: undefined,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ADAPTACAO_DE_CONTRATOS,
				},
				{
					title: ServicesCenterTitlesEnum.REGISTRATION_CHANGE,
					icon: 'user-check',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ALTERACAO_CADASTRAL,
				},
				{
					title: ServicesCenterTitlesEnum.DEPENDENT_INCLUSION,
					icon: 'person-add',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_INCLUSAO_DE_DEPENDENTES,
				},
				{
					title: ServicesCenterTitlesEnum.CONTRACT_REACTIVATION,
					icon: 'task-filled',
					endpoint: isAdmContractReactivationEnabled
						? undefined
						: makeGetServiceCenterUrl,
					route: isAdmContractReactivationEnabled
						? ContractReactivationEnum.CONTRACT_REACTIVATION
						: undefined,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId:
						ExternalServicesTaggingEnum.CLICK_REATIVACAO_DE_CONTRATO_CANCELADO_POR_INADIMPLENCIA,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_PLAN_HOLDER,
					icon: 'join-left-filled',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MUDAR_TITULAR_DO_PLANO,
				},
				{
					title: ServicesCenterTitlesEnum.PAD_ADHESION,
					icon: 'manage-account-filled',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ADESAO_AO_PAD,
				},
			],
		},
	];

	const NEW_SERVICES_LIST: ServiceList[] = [
		{
			category: CategoriesEnum.HEALTH_APPOINTMENTS,
			list: [
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_HEALTH,
					icon: 'pin-drop-filled',
					route: GuidesEnum.SERVICE_NETWORK_HEALTH,
					show:
						showNonGndiHealthServices &&
						!isResponsibleWithoutBenefits &&
						!congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_MEDICO,
				},
				{
					title: ServicesTitlesEnum.UNITS,
					icon: 'location-on-filled',
					endpoint: makeGetUnitsUrl,
					show: isGndiSaoPaulo && !isResponsibleWithoutBenefits && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_REDE_PROPRIA,
				},
				{
					title: ServicesTitlesEnum.TOKEN,
					icon: 'key-filled',
					route: TokenEnum.TOKEN,
					show: isGndiSaoPaulo,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_TOKEN,
				},
				{
					title: ServicesTitlesEnum.MY_HOSPITALIZATION,
					icon: 'local-hospital-filled',
					endpoint: isMyHospitalizationFeatureFlagEnabled
						? undefined
						: makeGetHospitalizationUrl,
					route: isMyHospitalizationFeatureFlagEnabled
						? MyHospitalizationFlowEnum.HOME
						: undefined,
					show: showHospitalizationService && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MINHA_INTERNACAO,
				},
			],
		},
		{
			category: CategoriesEnum.ODONTO_APPOINTMENTS,
			list: [
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_ODONTO,
					icon: 'pin-drop-filled',
					route: GuidesEnum.SERVICE_NETWORK_ODONTO,
					show: !healthOnly && !isResponsibleWithoutBenefits && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_ODONTO,
				},
				{
					title: ServicesTitlesEnum.FRANCHISES_TABLE,
					icon: 'table-rows-filled',
					route: FranchisesTableEnum.FRANCHISES_TABLE,
					show: showFranchisesTable && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_TABELA_FRANQUIAS,
				},
				{
					title: ServicesTitlesEnum.REFUND_ODONTO,
					icon: 'odonto-exchange-filled',
					endpoint: makeGetRefundOdonto,
					show: showRefundOdonto && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_REEMBOLSO_ODONTO,
				},
			],
		},
		{
			category: CategoriesEnum.FINANCIAL_AND_DOCS,
			list: [
				{
					title: showBemobiFlow,
					icon: 'receipt-long-filled',
					route: MyPaymentsEnum.OPEN_BILLS,
					show: isIndividualPlan && !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_BOLETOS,
				},
				{
					title: ServicesTitlesEnum.COPAYMENT_STATEMENT,
					icon: 'text-box-outline',
					route: MyPaymentsEnum.COPARTICIPATION,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_EXTRATO_DE_CO_PARTICIPACAO,
				},
				{
					title: ServicesTitlesEnum.INCOME_TAX_STATEMENT,
					icon: 'lion-filled',
					route: MyPaymentsEnum.INCOME_TAX,
					show: showIrDemonstrative,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_DEMOSTRATIVO_IR,
				},
				{
					title: ExternalServicesTitlesEnum.INVOICES,
					icon: 'barcode-default',
					externalLink: getInvoicesNFLinkByProduct(dataHostNameState.product),
					show: showInvoicesOption && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_NOTAS_FISCAIS,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_DUE_DATE,
					icon: 'event-repeat',
					endpoint: isAdmChangeDueDateEnabled ? undefined : makeGetServiceCenterUrl,
					route: isAdmChangeDueDateEnabled
						? ChangeDueDateEnum.CHANGE_DUE_DATE
						: undefined,
					show: showServiceCenterLinks && !congenereEligibility && isHolder && !odontoOnly,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_ALTERAR_DATA_DE_VENCIMENTO,
				},
				{
					title: ExternalServicesTitlesEnum.DEBT_SETTLEMENT_STATEMENT,
					icon: 'gavel-filled',
					endpoint: makeGetDebtSettlementStatementUrl,
					show: true,
					type: ServicesTypesEnum.OTHERS,
					dataTagId:
						ExternalServicesTaggingEnum.CLICK_DEMONSTRATIVO_DE_QUITACAO_DE_DEBITOS,
				},
				{
					title: ServicesCenterTitlesEnum.DECLARATIONS_AND_DOCUMENTS,
					icon: 'pending-action',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_DECLARACOES_DOCUMENTOS,
				},
				{
					title: ExternalServicesTitlesEnum.BENEFICIARY_DOCUMENT,
					icon: 'document-scanner',
					endpoint: makeGetBeneficiaryDocumentUrl,
					show: isGndiSaoPaulo,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_DOCS_E_FORMS,
				},
				{
					title: ExternalServicesTitlesEnum.FINANCIAL_SUPPORT,
					icon: 'chat-filled',
					externalLink: financialSupportWhatsappLink,
					show: !congenereEligibility && !(businessPlan && isHidePaymentSupportEnabled),
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ATENDIMENTO_FINANCEIRO_WHATSAPP,
				},
			],
		},
		{
			category: CategoriesEnum.PERKS,
			list: [
				{
					title: ServicesTitlesEnum.ADDITIONAL_COVERAGE,
					icon: 'circular-star',
					endpoint: makeGetAdditionalCoverage,
					show: showBenefitsForGndiSaoPaulo,
					type: ServicesTypesEnum.OTHERS,
					openInIframe: true,
					dataTagId: ExternalServicesTaggingEnum.CLICK_COBERTURAS_ADICIONAIS,
				},
				{
					title: ServicesTitlesEnum.PERKS_CLUB,
					icon: 'basket',
					endpoint: makeGetPerksClubLink,
					show: !isResponsibleWithoutBenefits && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CLUBE_DE_VANTAGENS,
				},
				{
					title: ServicesTitlesEnum.RECOMMENDATION,
					icon: 'add-reaction-filled',
					endpoint: makeGetWhoIndicatesLink,
					disableCache: true,
					show: !isResponsibleWithoutBenefits && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_QUEM_INDICA,
				},
				{
					title: ServicesTitlesEnum.CUIDAE,
					icon: 'percent-discount',
					route: PerksEnum.PERKS,
					show: isLifePlaceEnabled && !congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CUIDAE,
				},
			],
		},
		{
			category: CategoriesEnum.PORTABILITY_AND_PERMANENCE,
			list: [
				{
					title: ExternalServicesTitlesEnum.PIN_SS,
					icon: 'keyboard-filled',
					endpoint: makeGetPinSsUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_PIN_SS,
				},
				{
					title: ExternalServicesTitlesEnum.STAY_LETTER,
					icon: 'mark-as-read',
					endpoint: makeGetStayLetterUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					isBlob: true,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_PERMANENCIA,
				},
				{
					title: ExternalServicesTitlesEnum.PORTABILITY_CARD,
					icon: 'mark-as-unread',
					endpoint: makeGetPortabilityCardUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_DE_PORTABILIDADE,
				},
			],
		},
		{
			category: CategoriesEnum.MANAGEMENT,
			list: [
				{
					title: ServicesCenterTitlesEnum.CHANGE_PLAN,
					icon: 'books-filled',
					endpoint: isAdmChangePlanEnabled ? undefined : makeGetServiceCenterUrl,
					route: isAdmChangePlanEnabled ? ChangePlanEnum.CHANGE_PLAN : undefined,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MUDANCA_DE_PLANO,
				},
				{
					title: ServicesCenterTitlesEnum.CONTRACT_ADAPTATION,
					icon: 'source-filled',
					endpoint: isAdmContractAmendmentEnabled ? undefined : makeGetServiceCenterUrl,
					route: isAdmContractAmendmentEnabled
						? ContractAdaptationEnum.CONTRACT_ADAPTATION
						: undefined,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ADAPTACAO_DE_CONTRATOS,
				},
				{
					title: ServicesCenterTitlesEnum.REGISTRATION_CHANGE,
					icon: 'user-check',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ALTERACAO_CADASTRAL,
				},
				{
					title: ServicesCenterTitlesEnum.DEPENDENT_INCLUSION,
					icon: 'person-add',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_INCLUSAO_DE_DEPENDENTES,
				},
				{
					title: ServicesCenterTitlesEnum.CONTRACT_REACTIVATION,
					icon: 'task-filled',
					endpoint: isAdmContractReactivationEnabled
						? undefined
						: makeGetServiceCenterUrl,
					route: isAdmContractReactivationEnabled
						? ContractReactivationEnum.CONTRACT_REACTIVATION
						: undefined,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId:
						ExternalServicesTaggingEnum.CLICK_REATIVACAO_DE_CONTRATO_CANCELADO_POR_INADIMPLENCIA,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_PLAN_HOLDER,
					icon: 'join-left-filled',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MUDAR_TITULAR_DO_PLANO,
				},
				{
					title: ServicesCenterTitlesEnum.PAD_ADHESION,
					icon: 'manage-account-filled',
					endpoint: makeGetServiceCenterUrl,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ADESAO_AO_PAD,
				},
			],
		},
		{
			category: CategoriesEnum.SUPPORT,
			list: [
				{
					title: ServicesTitlesEnum.SERVICE_CHANNELS,
					icon: 'help-filled',
					route: HelpCenterEnum.HELP,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_PRECISA_DE_AJUDA,
					keywords: ['ajuda'],
				},
				{
					title: ExternalServicesTitlesEnum.SUPPORT,
					icon: 'verified-user',
					endpoint: helpDeskOrSupportEndpoint,
					show:
						showForGndiMinasOrSaoPaulo &&
						!isResponsibleWithoutBenefits &&
						isShowOmbudsmanInChannelsEnabled &&
						!congenereEligibility,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_OUVIDORIA,
				},
				{
					title: ServicesTitlesEnum.CONSULT_PROTOCOLS,
					icon: 'content-paste-search-filled',
					route: ConsultProtocolsEnum.CONSULT_PROTOCOLS,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_CONSULTA_DE_PROTOCOLOS,
				},
			],
		},
	];

	const SERVICES_LIST = isAccordionServicesFeatureFlagEnabled
		? NEW_SERVICES_LIST
		: OLD_SERVICES_LIST;

	const FILTERED_LIST = SERVICES_LIST.map(category => {
		const isSectionMatch = findItemByQuery({
			item: category.category,
			query,
		});

		const shouldIncludeItem = (item: Service) =>
			item?.show &&
			(isSectionMatch ||
				findItemByQuery({
					item: item?.title,
					query,
					keywords: item?.keywords,
				}));

		return {
			...category,
			list: category.list.filter(shouldIncludeItem),
		};
	});

	const categorizedServices = FILTERED_LIST.reduce(
		(acc, serviceCategory) => {
			acc[serviceCategory.category] = serviceCategory;
			return acc;
		},
		{} as Record<CategoriesEnum, ServiceList>,
	);

	const healthAppointmentsServices = categorizedServices[CategoriesEnum.HEALTH_APPOINTMENTS];
	const odontoAppointmentsServices = categorizedServices[CategoriesEnum.ODONTO_APPOINTMENTS];
	const financialAndDocsServices = categorizedServices[CategoriesEnum.FINANCIAL_AND_DOCS];
	const perksServices = categorizedServices[CategoriesEnum.PERKS];
	const portabilityAndPermanenceServices =
		categorizedServices[CategoriesEnum.PORTABILITY_AND_PERMANENCE];
	const supportServices = categorizedServices[CategoriesEnum.SUPPORT];
	const managementServices = categorizedServices[CategoriesEnum.MANAGEMENT];

	const isServiceListEmpty = FILTERED_LIST.every(item => item?.list?.length === 0);

	const firstColumnIsEmpty =
		!healthAppointmentsServices?.list.length &&
		!odontoAppointmentsServices?.list.length &&
		!financialAndDocsServices?.list.length;

	const secondColumnIsEmpty =
		!perksServices?.list.length &&
		!portabilityAndPermanenceServices?.list.length &&
		!supportServices?.list.length;

	const thirdColumnIsEmpty = !managementServices?.list.length;

	return {
		healthAppointmentsServices,
		odontoAppointmentsServices,
		financialAndDocsServices,
		perksServices,
		portabilityAndPermanenceServices,
		supportServices,
		managementServices,
		isServiceListEmpty,
		firstColumnIsEmpty,
		secondColumnIsEmpty,
		thirdColumnIsEmpty,
	};
}
